/**
 * StateManager v2 — stateContract-based access control.
 *
 * Wraps SymbolStore + ComputeEvaluator with security enforcement:
 * - @{localVar}: always allowed
 * - state.json keys: allowed
 * - outputSymbols paths: allowed
 * - _system.*: read-only
 * - Everything else: SecurityError on write
 */

import type { StateContract, BlockState } from "@cogo/types-v2"
import { SymbolStore } from "./symbol-store"
import { ComputeEvaluator, toNum } from "./compute-evaluator"
import { StateContractEnforcer } from "./security/access-control"
import type { CacheStats } from "./expression-cache"
import type { AuditEntry } from "./security/audit-log"
import { SecurityError } from "@cogo/types-v2"

/**
 * Write-governance mode (Step 4). See docs/agentic/runtime/step4-checkwrite-governance-design.md.
 * - off:     skip stateContract enforcement (today's `permissive: true`). Zero overhead.
 * - audit:   run checkWrite but DON'T throw — record the violation via onViolation, allow the write.
 * - enforce: checkWrite throws SecurityError on an undeclared write (fail-closed).
 */
export type GovernanceMode = "off" | "audit" | "enforce"

export interface StateDeclaration {
  /** Declared default value. Used for lazy init on first read. */
  default: unknown
  /** Optional declared type (informational for dev-mode warnings). */
  type?: string
}

export interface StateManagerOptions {
  /**
   * @deprecated Prefer `mode`. Back-compat shim: `true` ≡ mode 'off', `false`/absent ≡ mode 'enforce'.
   * When true, skip stateContract enforcement (for global app-level StateManager).
   */
  permissive?: boolean
  /**
   * Write-governance mode (Step 4). Overrides `permissive` when set. Default when neither is set:
   * 'enforce'. AppRuntimeCore mounts pass 'off' until a runtime opts in (COGO_RUNTIME_GOVERNANCE).
   */
  mode?: GovernanceMode
  /** Called once per audit-mode violation (no-op in 'off'/'enforce'). Receives the audit entry. */
  onViolation?: (entry: AuditEntry) => void
  /**
   * Path → declaration map used for lazy initialization on read.
   * When `get(path)` returns undefined and `path` has a declaration, the
   * declared default is written to the store and returned. Paths included
   * here don't need to be pre-initialized by the constructor; callers can
   * collect them from app.json globalState, block state.json, init flows,
   * or data.json source contracts.
   */
  declarations?: ReadonlyMap<string, StateDeclaration> | Record<string, StateDeclaration>
  /**
   * When true, log a warning the first time an undeclared path is read.
   * Intended for local development and tests only.
   */
  devMode?: boolean
}

export class StateManager {
  private store: SymbolStore
  private compute: ComputeEvaluator
  private enforcer: StateContractEnforcer
  private mode: GovernanceMode
  private onViolation?: (entry: AuditEntry) => void
  /** >0 while a trusted (runtime-internal) write scope is active — guardWrite is skipped (Step 4 R6). */
  private trustedDepth = 0
  private declarations: Map<string, StateDeclaration>
  private devMode: boolean
  private warnedPaths: Set<string>

  // Wave 9A — legacy expression prefix aliases are enabled by default in
  // SymbolStore.get(). Set COGO_RUNTIME_LEGACY_PREFIX_ALIASES=0 to disable
  // (will break any corpus still using localState/state/response/ui prefixes).
  // Aliases operate at SymbolStore.get() only — writes always go to
  // canonical prefixes via the existing @ ↔ localState sync above.
  constructor(blockState: BlockState, contract: StateContract, options?: StateManagerOptions) {
    this.store = new SymbolStore()
    this.compute = new ComputeEvaluator(this)
    // Governance mode (Step 4). Back-compat: permissive:true → 'off', false/absent → 'enforce'.
    this.mode = options?.mode ?? (options?.permissive ? "off" : "enforce")
    this.onViolation = options?.onViolation
    this.devMode = options?.devMode ?? false
    this.warnedPaths = new Set()

    this.declarations = new Map()
    if (options?.declarations) {
      const source = options.declarations
      if (source instanceof Map) {
        for (const [k, v] of source) this.declarations.set(k, v)
      } else {
        for (const [k, v] of Object.entries(source)) this.declarations.set(k, v)
      }
    }

    const stateObj = (blockState && typeof blockState === "object" && "state" in blockState && blockState.state && typeof blockState.state === "object")
      ? blockState.state
      : (blockState as any) ?? {}

    const ownStateKeys = Object.keys(stateObj)
    this.enforcer = new StateContractEnforcer(contract, ownStateKeys)

    // Initialize state variables from state.json
    for (const [key, def] of Object.entries(stateObj)) {
      const varDef = def as any
      if (varDef && typeof varDef === "object" && varDef.default !== undefined) {
        this.store.set(key, structuredClone(varDef.default))
      }
      // Also register as a declaration so lazy-init can restore after setState(null).
      if (!this.declarations.has(key) && varDef && typeof varDef === "object") {
        this.declarations.set(key, { default: varDef.default, type: varDef.type })
      }
    }

    // Initialize local state from stateContract.localState
    for (const [key, def] of Object.entries(contract?.localState ?? {})) {
      if (def.default !== undefined) {
        this.store.set(`@${key}`, structuredClone(def.default))
        // Also write under localState.key so #{localState.X} expressions resolve
        this.store.set(`localState.${key}`, structuredClone(def.default))
      }
    }
  }

  /**
   * Lazy-init helper: if path has a declaration and is currently undefined,
   * clone the declared default into the store. Returns whether init happened.
   */
  private lazyInitFromDeclaration(path: string): boolean {
    const decl = this.declarations.get(path)
    if (!decl || decl.default === undefined) return false
    const clone = structuredClone(decl.default)
    // Use setQuiet to avoid firing listeners mid-read (prevents render loops).
    this.store.setQuiet(path, clone)
    return true
  }

  get(path: string): unknown {
    const cleanPath = path.startsWith("#") ? path.slice(1) : path
    const value = this.store.get(cleanPath)
    if (value !== undefined) return value

    // Lazy init from declaration.
    if (this.lazyInitFromDeclaration(cleanPath)) {
      return this.store.get(cleanPath)
    }

    // Undeclared read — optionally warn (once per path) in dev mode.
    if (this.devMode && !this.warnedPaths.has(cleanPath)) {
      this.warnedPaths.add(cleanPath)
      // eslint-disable-next-line no-console
      console.warn(`[cogo] Undeclared state path read: "${cleanPath}"`)
    }
    return undefined
  }

  /** Register a declaration after construction (for dynamic sources like data.json). */
  registerDeclaration(path: string, decl: StateDeclaration): void {
    this.declarations.set(path, decl)
  }

  /** Test/debug helper — check whether a path is declared. */
  hasDeclaration(path: string): boolean {
    const cleanPath = path.startsWith("#") ? path.slice(1) : path
    return this.declarations.has(cleanPath)
  }

  /**
   * Run `fn` as a TRUSTED runtime-internal write scope (Step 4 R6): writes inside bypass governance.
   * Use ONLY for the runtime's own bookkeeping — state seeding, route/_system/nav/theme/computed/
   * datasource writes — NEVER around user-flow execution (those must stay governed). Re-entrant.
   */
  runTrusted<T>(fn: () => T): T {
    this.trustedDepth++
    try {
      return fn()
    } finally {
      this.trustedDepth--
    }
  }

  /**
   * Single trusted write (Step 4 R6) — for runtime-internal writes that happen AFTER an `await`
   * (e.g. datasource results), where a `runTrusted` scope around the async callback would have
   * already exited. Bypasses governance for this one write.
   */
  setStateTrusted(path: string, value: unknown): void {
    this.runTrusted(() => this.setState(path, value))
  }

  /**
   * Write-governance gate (Step 4). trusted scope → no-op; 'off' → no-op; 'enforce' → checkWrite
   * throws on an undeclared write; 'audit' → checkWrite runs but the SecurityError is caught and
   * the violation reported via onViolation (the write is still applied). Non-Security errors propagate.
   */
  private guardWrite(path: string): void {
    // Runtime-internal (trusted) writes bypass governance; only user-flow writes are gated.
    if (this.trustedDepth > 0) return
    if (this.mode === "off") return
    if (this.mode === "enforce") {
      this.enforcer.checkWrite(path)
      return
    }
    // audit
    try {
      this.enforcer.checkWrite(path)
    } catch (err) {
      if (err instanceof SecurityError) {
        if (this.onViolation) {
          const entries = this.enforcer.getAuditEntries()
          const last = entries[entries.length - 1]
          if (last) this.onViolation(last)
        }
      } else {
        throw err
      }
    }
  }

  /** Set state with stateContract enforcement (governed by `mode`). Throws SecurityError in 'enforce'. */
  setState(path: string, value: unknown): void {
    // Strip leading # (v1 compat: target="#appData.cart.items")
    const cleanPath = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(cleanPath)
    this.store.set(cleanPath, value)
    // Sync @key ↔ localState.key for #{localState.X} expression support
    if (cleanPath.startsWith("@")) {
      this.store.set(`localState.${cleanPath.slice(1)}`, value)
    } else if (cleanPath.startsWith("localState.")) {
      this.store.set(`@${cleanPath.slice(11)}`, value)
    }
  }

  /** Set state without notifying listeners. Still enforces stateContract (unless permissive). */
  setStateQuiet(path: string, value: unknown): void {
    const cleanPath = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(cleanPath)
    this.store.setQuiet(cleanPath, value)
    // Sync @key ↔ localState.key for #{localState.X} expression support
    if (cleanPath.startsWith("@")) {
      this.store.setQuiet(`localState.${cleanPath.slice(1)}`, value)
    } else if (cleanPath.startsWith("localState.")) {
      this.store.setQuiet(`@${cleanPath.slice(11)}`, value)
    }
  }

  /** Register namespace alias — delegates to SymbolStore. */
  registerNamespaceAlias(namespace: string): void {
    this.store.registerNamespaceAlias(namespace)
  }

  appendTo(path: string, value: unknown): void {
    const p = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(p)
    const arr = this.store.get(p)
    if (Array.isArray(arr)) {
      this.store.set(p, [...arr, value])
    } else if (arr == null) {
      // Array doesn't exist yet — initialise with the single item.
      this.store.set(p, [value])
    }
  }

  removeWhere(path: string, predicate: (item: unknown) => boolean): void {
    const p = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(p)
    const arr = this.store.get(p)
    if (Array.isArray(arr)) {
      this.store.set(p, arr.filter((item) => !predicate(item)))
    }
  }

  updateWhere(
    path: string,
    predicate: (item: unknown) => boolean,
    update: Record<string, unknown>,
  ): void {
    const p = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(p)
    const arr = this.store.get(p)
    if (Array.isArray(arr)) {
      this.store.set(
        p,
        arr.map((item) => {
          if (!predicate(item)) return item
          const resolved: Record<string, unknown> = {}
          const itemObj = item as Record<string, unknown>
          for (const [key, val] of Object.entries(update)) {
            if (typeof val === "string" && val.includes("#{")) {
              const single = /^#\{(.+)\}$/.exec(val.trim())
              if (single && !single[1].includes("#{")) {
                // A single `#{expr}` value → preserve the NATURAL type (boolean / number / string /
                // object), so a boolean toggle `#{!item.completed}` stays a boolean instead of being
                // coerced to 0/1 by the string-template + Number() path below.
                const r = single[1].trim()
                resolved[key] = /^item\.\w+$/.test(r)
                  ? getNestedValue(itemObj, r.substring(5))
                  : this.resolveOnMatchExpr(r, itemObj, false)
              } else {
                // A string template (text around / multiple `#{...}`) → stringify, then numeric-coerce.
                const result = val.replace(/#\{([^}]+)\}/g, (_, expr) => {
                  const r = (expr as string).trim()
                  if (/^item\.\w+$/.test(r)) {
                    const propPath = r.substring(5)
                    const v = getNestedValue(itemObj, propPath)
                    return String(v ?? "")
                  }
                  const evaluated = this.resolveOnMatchExpr(r, itemObj)
                  return String(evaluated ?? "")
                })
                const numVal = Number(result)
                resolved[key] = !isNaN(numVal) && result.trim() !== "" ? numVal : result
              }
            } else {
              resolved[key] = val
            }
          }
          return { ...itemObj, ...resolved }
        }),
      )
    }
  }

  upsertWhere(
    path: string,
    matchExpr?: string,
    onMatch?: Record<string, unknown>,
    onNew?: Record<string, unknown>,
  ): void {
    const cleanPath = path.startsWith("#") ? path.slice(1) : path
    this.guardWrite(cleanPath)
    const arr = this.store.get(cleanPath)
    if (!Array.isArray(arr)) {
      if (onNew) this.store.set(cleanPath, [onNew])
      return
    }

    const idx = matchExpr
      ? arr.findIndex((item) => this.compute.evalPredicate(matchExpr, item))
      : -1

    if (idx >= 0 && onMatch) {
      const updated = { ...(arr[idx] as object) } as Record<string, unknown>
      const itemObj = arr[idx] as Record<string, unknown>
      for (const [key, val] of Object.entries(onMatch)) {
        if (typeof val === "string" && val.includes("#{")) {
          const resolved = val.replace(/#\{([^}]+)\}/g, (_, expr) => {
            const r = (expr as string).trim()
            if (/^item\.\w+$/.test(r)) {
              const propPath = r.substring(5)
              const v = getNestedValue(itemObj, propPath)
              return String(v ?? "")
            }
            const evaluated = this.resolveOnMatchExpr(r, itemObj)
            return String(evaluated ?? "")
          })
          const numVal = Number(resolved)
          updated[key] = !isNaN(numVal) && resolved.trim() !== "" ? numVal : resolved
        } else {
          updated[key] = val
        }
      }
      const newArr = [...arr]
      newArr[idx] = updated
      this.store.set(cleanPath, newArr)
    } else if (onNew) {
      this.store.set(cleanPath, [...arr, onNew])
    }
  }

  private resolveOnMatchExpr(expr: string, item: Record<string, unknown>, coerceNumeric = true): unknown {
    let processed = expr.replace(/\bitem\.(\w+(?:\.\w+)*)/g, (_, field) => {
      const val = getNestedValue(item, field)
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const n = parseFloat(val)
        return isNaN(n) ? JSON.stringify(val) : n.toString()
      }
      return String(val ?? "null")
    })

    processed = processed.replace(/\b([a-zA-Z_]\w*(?:\.\w+)+)/g, (match) => {
      if (/^\d/.test(match)) return match
      const val = this.store.get(match)
      if (val !== undefined) {
        if (typeof val === "number") return val.toString()
        if (typeof val === "string") {
          const n = parseFloat(val)
          return isNaN(n) ? JSON.stringify(val) : n.toString()
        }
        return String(val)
      }
      return match
    })

    const trimmed = processed.trim()
    const asNum = parseFloat(trimmed)
    if (!isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(trimmed)) return asNum

    try {
      const evaluated = this.compute.evaluate(processed)
      // coerceNumeric (default) keeps the legacy number-forcing for the string-template callers;
      // the single-`#{expr}` updateWhere path passes false to preserve booleans/strings/objects.
      return coerceNumeric ? toNum(evaluated) : evaluated
    } catch {
      return this.store.get(expr) ?? processed
    }
  }

  resolve(template: string): unknown {
    if (typeof template !== "string") return template

    if (template.includes("?.")) {
      template = template.replace(/\?\./g, ".")
    }

    const localMatch = /^@\{([^}]+)\}$/.exec(template)
    if (localMatch) {
      const resolved = this.store.resolve(template)
      if (resolved === template) return ""
      return resolved
    }

    if (template.includes("@{") && !template.includes("#{")) {
      return template.replace(/@\{([^}]+)\}/g, (_, inner) => {
        const path = (inner as string).trim()
        const val = this.store.get(`@${path}`)
        return val != null ? String(val) : ""
      })
    }

    const pureMatch = /^#\{([^}]+)\}$/.exec(template)
    if (pureMatch) {
      const expr = pureMatch[1].trim()
      if (/^!?[\w.]+$/.test(expr)) {
        const resolved = this.store.resolve(template)
        if (resolved === template || resolved === undefined) return ""
        return resolved
      }
      try {
        return this.compute.evaluate(expr)
      } catch {
        const resolved = this.store.resolve(template)
        if (resolved === template || resolved === undefined) return ""
        return resolved
      }
    }

    if (template.includes("#{")) {
      let result = template.replace(/#\{([^}]+)\}/g, (_, inner) => {
        const expr = (inner as string).trim()
        if (/^!?[\w.]+$/.test(expr)) {
          const path = expr.startsWith("!") ? expr.slice(1) : expr
          const val = this.store.get(path)
          if (expr.startsWith("!")) return String(!val)
          return val != null ? String(val) : ""
        }
        try {
          const r = this.compute.evaluate(expr)
          return r != null ? String(r) : ""
        } catch {
          const val = this.store.get(expr)
          return val != null ? String(val) : ""
        }
      })
      if (result.includes("@{")) {
        result = result.replace(/@\{([^}]+)\}/g, (_, inner) => {
          const path = (inner as string).trim()
          const val = this.store.get(path)
          return val != null ? String(val) : ""
        })
      }
      return result
    }

    return template
  }

  subscribe(path: string, listener: (value: unknown) => void): () => void {
    return this.store.subscribe(path, listener)
  }

  getSnapshot(): Record<string, unknown> {
    return this.store.getSnapshot()
  }

  getSymbolStore(): SymbolStore {
    return this.store
  }

  getCompute(): ComputeEvaluator {
    return this.compute
  }

  getCacheStats(): CacheStats {
    return this.compute.getCacheStats()
  }

  clearExpressionCache(): void {
    this.compute.clearCache()
  }

  /** Get security audit log entries. */
  getAuditLog(): AuditEntry[] {
    return this.enforcer.getAuditEntries()
  }

  /** Clear security audit log. */
  clearAuditLog(): void {
    this.enforcer.clearAuditLog()
  }
}

function getNestedValue(obj: unknown, path: string): unknown {
  const keys = path.split(".")
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}
